/**
 * @typedef {Object} VariantInventoryData
 * @property {boolean} available
 * @property {string|null} management - Equal to "shopify" when it's a string.
 * @property {string|null} policy - Equal to "allow" or "deny" when inventory_management is "shopify".
 * @property {number} quantity
 */

/**
 * @typedef {Object} Variant
 * @property {number} id
 * @property {string} title
 * @property {number} price (cents)
 * @property {VariantInventoryData} inventoryData
 * @property {Array<string>} options
 * @property {string|null} option1
 * @property {string|null} option2
 * @property {string|null} option3
 * @property {{ alt: string|null, src: string }|null} featured_image - src: E.g. //cdn.shopify.com/s/....jpg?v=12323423
 *
 */

/**
 * @typedef {Object} ProductOption
 * @property {string} name
 * @property {Array<string>} values
 */

/**
 * @typedef {Object} Product
 * @property {number} id
 * @property {string} handle
 * @property {string} title
 * @property {string[]} options
 * @property {Array<Variant>} variants
 * @property {Array<string>} tags
 * @property {Array<string>} images - E.g. //cdn.shopify.com/s/....jpg?v=12323423
 * @property {string} featured_image - E.g. //cdn.shopify.com/s/....jpg?v=12323423
 * @property {string} type
 */

/**
 * @typedef {Object} Design
 * @property {string} title
 * @property {string} image
 * @property {Array<{ handle: string, type: string }>} products
 */

/**
 * @typedef {Object} Department
 * @property {string} title
 * @property {Array<string>} styles
 */

/**
 * @typedef {Object} Category
 * @property {string} title
 * @property {string} handle
 */

/**
 * @typedef {object} PlugDesignAndDepartmentsJson
 * @property {Design} design
 * @property {Array<Department>} departments
 * @property {Array<Category>} categories
 */

/**
 * @typedef {Object} PlugProductJson
 * @property {Product} product
 * @property {Variant} activeVariant
 * @property {string} printfulProductId
 */

(function () {
    class StyleTooltip extends HTMLElement {
        constructor() {
            super();
            PLUG.utils.bindThisToAllMethods(this);
            this.elements = {
                toggleButton: this.querySelector(".style-tooltip__toggle-button"),
                content: this.querySelector(".style-tooltip__content"),
                closeButton: this.querySelector(".style-tooltip__close-button"),
            };
        }

        connectedCallback() {
            const { toggleButton, closeButton } = this.elements;

            toggleButton.addEventListener("click", () => {
                toggleButton.disabled = true;
                if (!this.classList.contains("is-active")) {
                    this.showTooltip(() => { toggleButton.disabled = false; });
                } else {
                    this.hideTooltip(() => { toggleButton.disabled = false; });
                }
            }, false);

            closeButton.addEventListener("click", () => {
                closeButton.disabled = true;
                this.hideTooltip(() => { closeButton.disabled = false; });
            });
        }

        onDocumentClick(e) {
            this.hideTooltip();
            // if (
            //     !e.target.closest("style-tooltip")
            //     || e.target.closest(".style-tooltip__toggle-button")
            //     || e.target.closest(".style-tooltip__close-button")
            // ) {
            //     this.hideTooltip();
            // }
        }

        showTooltip(cb) {
            this.elements.content.classList.remove("is-hidden");
            this.elements.toggleButton.setAttribute("aria-expanded", "true");
            PLUG.utils.afterNextFrame(() => {
               this.classList.add("is-active");
               document.addEventListener("click", this.onDocumentClick);
               if (cb) return cb();
            });
        }

        hideTooltip(cb) {
            this.classList.remove("is-active");
            setTimeout(() => {
                this.elements.content.classList.add("is-hidden");
                this.elements.toggleButton.setAttribute("aria-expanded", "false");
                document.removeEventListener("click", this.onDocumentClick);
                if (cb) return cb();
            }, 300);
        }
    }
    customElements.define("style-tooltip", StyleTooltip);

    const liquid = PLUG.liquid;
    const rootUrl = liquid.rootUrl + (!liquid.rootUrl.endsWith("/") ? "/" : "");  // e.g. "/", "/es/", etc.
    const productSection = document.querySelector(".main-product-section");
    const productSectionId = productSection.id.replace("shopify-section-", "");
    let plugVariantPicker = document.querySelector("plug-variant-picker");
    const { design, departments, categories } = /** @type {PlugDesignAndDepartmentsJson} */
        JSON.parse(document.getElementById("PlugDesignAndDepartmentsJson").innerHTML);
    const productSectionData = /** @type PlugProductJson */
        JSON.parse(document.getElementById("PlugProductJson").innerHTML);
    let activeDepartmentIndex = plugVariantPicker.getActiveDepartmentIndex();
    let activeProduct = productSectionData.product;
    let activeVariant = productSectionData.activeVariant;
    const sections  = /** @type {Object.<string, HTMLElement>} */ {};
    sections[activeProduct.handle] = productSection.cloneNode(true);

    history.scrollRestoration = "manual";

    // The product-modal has a 'connectedCallback' method which moves the element to the end of the <body>.
    // By default, at the time that we clone the product section above, the product-modal has already moved itself
    // out of the product section.
    //
    // To combat this, we have added a data attribute called 'blockConnectedCallback' to the modal-dialog element
    // (which the product-modal extends), which will block any move attempts when set to "true". This allows us
    // to delay moving the element until after we have cloned the section.
    productSection.querySelector("product-modal").init();
    sections[activeProduct.handle].querySelector("product-modal").dataset.blockConnectedCallback = "false";

    (function init() {
        const previousUrl = document.referrer;
        const cookieMatch = document.cookie.match(/(?:^|; )plug_snorg_style_pref=([^;]*)/);
        const stylePref = cookieMatch ? decodeURIComponent(cookieMatch[1]) : undefined;

        // It will check if the previous URL is the same page or from a collection that specifies a department.
        // If it doesn't match, force the style to select the first option.
        if (stylePref === undefined && previousUrl !== window.location) {
            var default_style = true;
            if (previousUrl.includes('collections')) {
                const collectionHandle = previousUrl.split('collections/').pop().split('?')[0]
                function handleize(str) {
                    return str
                        .toLowerCase()
                        .replace(/['"]/g, '')            // remove quotes
                        .replace(/[^a-z0-9]+/g, '-')     // replace non-alphanumerics with hyphen
                        .replace(/^-+|-+$/g, '');        // trim hyphens
                }

                // Collect all titles and styles
                const handles = [];
                departments.forEach(category => {
                    handles.push(handleize(category.title));
                    category.styles.forEach(style => {
                        handles.push(handleize(style));
                    });
                });
                if (handles.includes(collectionHandle)) {
                    default_style = false;
                }
            }
        }
        if (default_style) {
            // Get the first style.
            const styleFields = document.querySelector('[data-option-name="Style"]');
            const newStyle = styleFields?.querySelector('input');
            PLUG.preferenceCookies.setStylePreference(newStyle.value);
            // PLUG.utils.setLoadingState(radioButtonLabel, true);

            // publish(PUB_SUB_EVENTS.productStyleChange, {
            //     data: {
            //         departmentIndex: activeDepartmentIndex,
            //         style: newStyle.value,
            //         isNewDepartment: (
            //             oldDepartmentIndex !== undefined
            //             && oldDepartmentIndex !== activeDepartmentIndex
            //         )
            //     }
            // });
            // if (newStyle != undefined) {
            //     newStyle.dataset.isInitial = "true";
            //     newStyle.click();
            //     newStyle.dispatchEvent(new Event('change'));
            // }
            updateStyle(newStyle.value, false).catch(err => console.error(err));
        } else {
            PLUG.preferenceCookies.setStylePreference(activeProduct.type);
        }

        subscribeToEvents();

        setInitialDepartment();

        // Since these requests are just made to optimize the UX of changing products (thus not urgent),
        // we set a timeout to move them to the back of the JS process 'queue'.
        setTimeout(fetchDepartmentSections, 0, activeDepartmentIndex);
    })();

    /**
     * This function handles situations where a style belongs to multiple departments, e.g. Men's and Women's.
     * In these cases, Men's will be selected by default in the liquid, so we need a way to select the
     * Women's department for cases where a user's department preference is set to Women's.
     *
     * We only change the department to match the preferences when the active style also belongs
     * to the preferred department. In other words, we never want to change the active style on page load,
     * only the active department.
     */
    function setInitialDepartment() {
        const activeDepartment = departments[activeDepartmentIndex];
        const preferredDepartmentTitle = PLUG.preferenceCookies.getPreferences().department;
        if (!preferredDepartmentTitle) return;
        const preferredDepartment = departments.find(department => department.title === preferredDepartmentTitle);
        if (
            !preferredDepartment
            || preferredDepartment.title === activeDepartment.title
            || !preferredDepartment.styles.includes(activeProduct.type)
        ) {
            return;
        }
        PLUG.elementClasses.PlugVariantPicker.setDepartment(preferredDepartmentTitle);
    }

    function subscribeToEvents() {
        subscribe(PUB_SUB_EVENTS.productDepartmentChange, (eventData) => {
            const { departmentIndex, radioButtonLabel, isInitialDepartmentChange } = eventData.data;
            PLUG.preferenceCookies.setDepartmentPreference(departments[departmentIndex].title);
            return updateDepartment(departmentIndex, radioButtonLabel, isInitialDepartmentChange).catch(err => console.error(err));
        });

        subscribe(PUB_SUB_EVENTS.productStyleChange, (eventData) => {
            const { style } = eventData.data;
            const isNewDepartment = (
                eventData.data.isNewDepartment !== undefined
                ? eventData.data.isNewDepartment
                : false
            );
            return updateStyle(style, isNewDepartment).catch(err => console.error(err));
        });

        subscribe(PUB_SUB_EVENTS.variantChange, (eventData) => {
            const { variant } = eventData.data;
            const selectedSize = determineVariantSize(activeProduct, variant);
            if (selectedSize) {
                PLUG.preferenceCookies.setSizePreference(selectedSize);
            }
            activeVariant = variant;
        });
    }

    /** @param {number} departmentIndex */
    function fetchDepartmentSections(departmentIndex) {
        const department = departments[departmentIndex];
        const departmentProducts = design.products.filter(designProduct => {
            return department.styles.includes(designProduct.type);
        });
        const handles = departmentProducts.map(product => product.handle);
        const handlesToFetch = handles.filter(handle => !(handle in sections));
        const promises = handlesToFetch.map(handle => {
            return PLUG.utils.fetchSection(productSectionId, `${rootUrl}products/${handle}`);
        });
        Promise.allSettled(promises).then(results => {
            results.forEach((result, resultIndex) => {
                if (result.status === "rejected") {
                    console.error(results.reason);
                } else {
                    sections[handlesToFetch[resultIndex]] = result.value;
                }
            });
        });
    }

    /**
     * @param {number} newDepartmentIndex
     * @param {HTMLLabelElement} radioButtonLabel
     * @param {boolean} isInitialDepartmentChange
     */
    async function updateDepartment(newDepartmentIndex, radioButtonLabel, isInitialDepartmentChange) {
        // Assert: activeProduct and activeVariant have not yet been affected by the department change.
        const newDepartment = departments[newDepartmentIndex];
        const oldStyle = activeProduct.type;
        const oldDepartmentIndex = activeDepartmentIndex;
        activeDepartmentIndex = newDepartmentIndex;

        if (isInitialDepartmentChange && newDepartment.styles.includes(oldStyle)) {
            PLUG.elementClasses.PlugVariantPicker.setStyles(
                plugVariantPicker,
                newDepartment.styles,
                newDepartment.styles.indexOf(oldStyle),
                newDepartment.title
            );
            // Some styles have a display name that depends on the active department. Thus, even though the style value
            // isn't changing here, we still update the legend text.
            const styleFieldset = document.querySelector(".plug-custom-variant-option[data-option-name=\"Style\"]");
            const activeInput = styleFieldset.querySelector("input:checked");
            if (activeInput) {
                const legendValueLabel = styleFieldset.querySelector("legend span");
                legendValueLabel.textContent = activeInput.dataset.displayName || activeInput.value;
            }
        } else {
            PLUG.utils.setLoadingState(radioButtonLabel, true);

            publish(PUB_SUB_EVENTS.productStyleChange, {
                data: {
                    departmentIndex: activeDepartmentIndex,
                    style: newDepartment.styles[0],
                    isNewDepartment: (
                        oldDepartmentIndex !== undefined
                        && oldDepartmentIndex !== activeDepartmentIndex
                    )
                }
            });

            // We add a delay here to let the active product's data/images load
            // before we load the non-active data.
            setTimeout(() => {
                fetchDepartmentSections(activeDepartmentIndex);
            }, 500);
        }
    }

    /**
     * @param {string} newStyle
     * @param {boolean} isNewDepartment
     * @return {Promise<void>}
     */
    async function updateStyle(newStyle, isNewDepartment) {
        const designProduct = design.products.find(product => product.type === newStyle);
        const newHandle = designProduct.handle;

        // Get the product section corresponding to the new product's handle, fetching it if we don't already have it.
        let fetchedSection = sections[newHandle];
        if (!fetchedSection) {
            console.warn(`Couldn't find existing section for handle: ${newHandle}`);
            fetchedSection = await PLUG.utils.fetchSection(productSectionId, `${rootUrl}products/${newHandle}`);
            sections[newHandle] = fetchedSection;
        }
        const newSection = fetchedSection.cloneNode(true);

        updateMediaGallery(newSection);

        // Update our activeProduct and activeVariant data.
        const newSectionData = JSON.parse(newSection.querySelector("#PlugProductJson").innerHTML);
        const printfulProductId = newSectionData.printfulProductId;
        const newProduct = newSectionData.product;
        activeVariant = determineActiveVariant(activeProduct, newProduct, isNewDepartment);
        activeProduct = newProduct;

        PLUG.preferenceCookies.setStylePreference(activeProduct.type);

        // const selectedSize = determineVariantSize(activeProduct, activeVariant);
        // console.log({VariantSize: selectedSize});
        // if (selectedSize) {
        //     PLUG.preferenceCookies.setSizePreference(selectedSize);
        // }

        updateVariantPicker(newSection);
        updateImageZoomModal(newSection);
        if (printfulProductId) {
            updatePrintfulEdt(printfulProductId);
        }
        updatePageTitle();
    }

    /**
     * @param {Product} oldProduct
     * @param {Product} newProduct
     * @param {boolean} isNewDepartment
     * @return {Variant}
     */
    function determineActiveVariant(oldProduct, newProduct, isNewDepartment=false) {
        const oldLowercaseOptionNames = oldProduct.options.map(option => option.toLowerCase());
        const oldSizeOptionIndex = oldLowercaseOptionNames.indexOf("size");
        const oldSize = activeVariant.options[oldSizeOptionIndex];
        const newLowercaseOptionNames = newProduct.options.map(option => option.toLowerCase());
        const newColorOptionIndex = newLowercaseOptionNames.indexOf("color");
        const newSizeOptionIndex = newLowercaseOptionNames.indexOf("size");

        const newProductColors = [];
        const newProductSizes = [];
        newProduct.variants.forEach(variant => {
            const color = variant.options[newColorOptionIndex];
            const size = variant.options[newSizeOptionIndex];
            if (!newProductColors.includes(color)) {
                newProductColors.push(color);
            }
            if (!newProductSizes.includes(size)) {
                newProductSizes.push(size);
            }
        });

        let matchingVariant;

        if (!isNewDepartment) {
            matchingVariant = newProduct.variants.find(variant => (
               variant.options[newSizeOptionIndex] === oldSize
            ));
        }

        return (
            matchingVariant
            ? matchingVariant
            : newProduct.variants[0]
        );
    }

    /** @param {HTMLElement} newSection */
    function updateMediaGallery(newSection) {
        const oldMediaGallery = productSection.querySelector("media-gallery");
        const newMediaGallery = newSection.querySelector("media-gallery");
        oldMediaGallery.style.opacity = "0";
        newMediaGallery.style.display = "none";
        PLUG.utils.replaceElement(oldMediaGallery, newMediaGallery);
        newMediaGallery.style.display = "block";
        PLUG.utils.afterNextFrame(() => {
            newMediaGallery.style.opacity = "1";
        });
    }

    /** @param {HTMLElement} newSection */
    function updateVariantPicker(newSection) {
        const oldPlugVariantPicker = productSection.querySelector("plug-variant-picker");
        plugVariantPicker = newSection.querySelector("plug-variant-picker");

        plugVariantPicker.classList.add("hidden");

        PLUG.utils.replaceElement(oldPlugVariantPicker, plugVariantPicker);

        PLUG.elementClasses.PlugVariantPicker.setStyles(
            plugVariantPicker,
            departments[activeDepartmentIndex].styles,
            departments[activeDepartmentIndex].styles.indexOf(activeProduct.type),
            departments[activeDepartmentIndex].title
        );

        // Make sure the correct department is selected.
        const departmentInputs = Array.from(plugVariantPicker.querySelectorAll("input[name=\"department\"]"));
        departmentInputs.forEach(input => {
            input.checked = (input.value === departments[activeDepartmentIndex].title);
        });
        if (departmentInputs.length) {
            const activeDepartmentLabel = departmentInputs[0].closest("fieldset").querySelector("legend span");
            activeDepartmentLabel.textContent = departments[activeDepartmentIndex].title;
        }

        PLUG.utils.waitUntilTrue(
            function () {
                return (typeof plugVariantPicker.updateActiveVariant === "function");
            },
            function doneWaiting(timedOut) {
                if (timedOut) {
                    console.error("Timed out waiting for plug-variant-picker to be connected to DOM.");
                    return;
                }
                plugVariantPicker.updateActiveVariant(
                    JSON.parse(JSON.stringify(activeProduct)),
                    JSON.parse(JSON.stringify(activeVariant))
                );
                plugVariantPicker.classList.remove("hidden");
            },
            60,
            10
        );
    }

    /** @param {HTMLElement} newSection */
    function updateImageZoomModal(newSection) {
        const oldProductModal = document.querySelector("product-modal.product-media-modal");
        const newProductModal = newSection.querySelector("product-modal.product-media-modal");
        PLUG.utils.replaceElement(oldProductModal, newProductModal);
    }

    /** @param {string | number} printfulProductId */
    function updatePrintfulEdt(printfulProductId) {
        PLUG.utils.getPrintfulEdt(printfulProductId).then(data => {
            let edtEle = document.querySelector(".product-form .edt");
            let { countryCode, html, is_success } = data;
            if (!is_success) {
                console.log(`Printful estimated delivery time request is unavailable for product with printful ID: ${printfulProductId}.`);
                if (edtEle) {
                    edtEle.classList.add("hidden");
                }
                return;
            }
            const iconImageUrl = window.pfEdtFlags[countryCode];
            html = html.replace("{img}", `<img src="${iconImageUrl}" style="height: 1em; width: 1.5em; margin-bottom: -0.125em;">`);

            if (edtEle) {
                edtEle.classList.remove("hidden");
            } else {
                edtEle = document.createElement("div");
                edtEle.className = "edt";
                edtEle.style.width = "100%";
                edtEle.style.textAlign = "center";
                edtEle.style.marginTop = "8px";
                edtEle.style.padding = "0px 8px";
                const form = document.querySelector(".product-form form[data-type=\"add-to-cart-form\"]");
                if (form) {
                    form.appendChild(edtEle);
                }
            }

            edtEle.innerHTML = html;
        }).catch(err => {
            console.error(err);
        });
    }

    function updatePageTitle() {
        const title = document.querySelector("title");
        title.textContent = `${activeProduct.title} – SnorgTees.com`;
    }

    /**
     * @param {Product} product
     * @param {Variant} variant
     * @return {string | null}
     */
    function determineVariantSize(product, variant) {
        const sizeOptionIndex = Math.max(product.options.indexOf("Size"), product.options.indexOf("size"));
        if (sizeOptionIndex === -1) return null;
        const size = variant[`option${sizeOptionIndex + 1}`];
        return size ? size : null;
    }
})();
